/**
 * Hit-effect sprites and square particles (W2-01a preview-gl; rendering.md R§5.2-R§5.3, R§8 step 8, S§4).
 *
 * Both live in UI space (y down). The engine already computed position, size, atlas frame, rotation and the
 * colour with the 1 -> 0.7 -> 0 alpha curve; this pass only draws:
 *  - sprite: a square [-half, half]^2 rotated by `rotDeg` (degrees, clockwise on the y-down screen, like the line),
 *    textured with frame `frame` of the hit_fx atlas (row-major, row 0 = top of the image, no half-texel inset);
 *    its (-half, -half) corner (top-left on screen) maps to the frame's top-left UV, so the sprite is upright;
 *  - particles: untextured, axis-aligned squares.
 * Every sprite is drawn before every particle (Phira draws all emitters' sprites, then the squares).
 */

import { colorByte } from '../gl/quadBatch';
import { beginQuads, type PassContext } from './passContext';

/** UV rectangle [u0, v0, u1, v1] of atlas frame `frame` in a cols x rows grid (frames past the end clamp). */
export function atlasFrameUv(frame: number, cols: number, rows: number): readonly [number, number, number, number] {
  const c = Math.max(1, Math.trunc(cols));
  const r = Math.max(1, Math.trunc(rows));
  const f = Math.max(0, Math.min(c * r - 1, Math.trunc(frame)));
  const col = f % c;
  const row = Math.floor(f / c);
  return [col / c, row / r, (col + 1) / c, (row + 1) / r];
}

export function drawHitFx(ctx: PassContext): void {
  const { list, batch } = ctx;
  const skin = ctx.skin;
  if (skin !== null && list.hitFx.length > 0) {
    const tex = ctx.textures.get(skin.images.hit_fx, 'clamp', true);
    if (tex !== null) {
      const [cols, rows] = skin.metrics.hitFx;
      beginQuads(ctx, ctx.uiToClip, tex);
      for (const s of list.hitFx) {
        const a = colorByte(s.color[3]);
        if (a === 0 || !(s.half > 0)) continue;
        const [u0, v0, u1, v1] = atlasFrameUv(s.frame, cols, rows);
        const rad = (s.rotDeg * Math.PI) / 180;
        const c = Math.cos(rad);
        const n = Math.sin(rad);
        // Rotation in y-down space (positive = clockwise on screen), then translation.
        batch.rect(
          [c, n, -n, c, s.x, s.y],
          -s.half,
          -s.half,
          s.half,
          s.half,
          u0,
          v0,
          u1,
          v1,
          colorByte(s.color[0]),
          colorByte(s.color[1]),
          colorByte(s.color[2]),
          a,
        );
      }
    }
  }
  if (list.particles.length > 0) {
    beginQuads(ctx, ctx.uiToClip, null);
    const I = [1, 0, 0, 1, 0, 0] as const;
    for (const p of list.particles) {
      const a = colorByte(p.color[3]);
      if (a === 0 || !(p.half > 0)) continue;
      batch.rect(
        I,
        p.x - p.half,
        p.y - p.half,
        p.x + p.half,
        p.y + p.half,
        0,
        0,
        1,
        1,
        colorByte(p.color[0]),
        colorByte(p.color[1]),
        colorByte(p.color[2]),
        a,
      );
    }
  }
}
